import { advancedSearchApi, handleError } from '@ardoq/api';
import { QueryBuilderQuery } from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import { ObservableState } from '@ardoq/rxbeach';
import { scopeDataOperations } from '@ardoq/scope-data';
import { isEqual } from 'lodash';
import {
  distinctUntilChanged,
  filter,
  from,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { toScopeDataWithSortedComponentsAndWorkspacesByNames } from './toScopeDataWithSortedComponentsAndWorkspacesByNames';
import { getCurrentLocale } from '@ardoq/locale';
import { EnhancedScopeData } from '@ardoq/data-model';
import { getScopeDataWithWorkspaceOptionsStream } from './getScopeDataWithWorkspaceOptions';
import { DropdownOption } from '@ardoq/dropdown-menu';
import { LoadErrorMessageKey, LoadingMessageKey } from './loadStatusKey';

const toLoadingMessage = (
  query: QueryBuilderQuery | null
): LoadingMessageKey | null => (query ? 'query' : null);

type QuerySource = {
  query: QueryBuilderQuery | null;
};

export const getScopeDataStreamWithWorkspaceOptions = <T extends QuerySource>(
  querySource$: Observable<T> | ObservableState<T>,
  actionNamespace: string
) => {
  const query$: Observable<QueryBuilderQuery | null> = querySource$.pipe(
    map(({ query }) => query),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const loadScopeData$ = query$.pipe(
    switchMap(query =>
      query
        ? advancedSearchApi.scopeData({ query, minimal: true })
        : of(scopeDataOperations.getEmpty())
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const loadingScopeDataNotification$: Observable<LoadingMessageKey | null> =
    merge(
      query$.pipe(map(toLoadingMessage)),
      loadScopeData$.pipe(map(() => null)),
      from([null])
    );

  const loadScopeDataError$: Observable<LoadErrorMessageKey | null> = merge(
    loadScopeData$.pipe(
      filter(isArdoqError),
      map((): LoadErrorMessageKey => 'query')
    ),
    query$.pipe(map(() => null)),
    from([null])
  );

  const scopeData$ = loadScopeData$.pipe(handleError());
  const scopeDataWithWorkspaceOptions$ = getScopeDataWithWorkspaceOptionsStream(
    scopeData$,
    actionNamespace
  );

  const workspaceOptions$: Observable<{
    options: DropdownOption[];
  }> = scopeDataWithWorkspaceOptions$.pipe(
    map(state => ({ options: state.workspaceOptions }))
  );

  const enhancedScopeData$: Observable<EnhancedScopeData> =
    scopeDataWithWorkspaceOptions$.pipe(
      map(({ scopeData }) => {
        const sortedScopeData =
          toScopeDataWithSortedComponentsAndWorkspacesByNames(
            scopeData,
            getCurrentLocale()
          );
        return scopeDataOperations.enhance(sortedScopeData);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  return {
    loadingScopeDataNotification$,
    loadScopeDataError$,
    enhancedScopeData$,
    workspaceOptions$,
  };
};
